import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../Api";
import { useDashboardTimeRange } from "../hooks/useDashboardTimeRange";
import DashboardTimeRangeControls from "./DashboardTimeRangeControls";
import { useAuth } from "../context/AuthContext";
import { useLogout } from "../hooks/useLogout";
import "./Dashboard.css";
import UserTimezoneSelect from "./UserTimezoneSelect";
import AskCommrayAi from "./AskCommrayAi";

function Dashboards() {
  const [dashboards, setDashboards] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [timeRangeOpen, setTimeRangeOpen] = useState(true);
  const {
    selection: timeSelection,
    updateSelection: updateTimeSelection
  } = useDashboardTimeRange(undefined, { autoRefresh: false });
  const navigate = useNavigate();
  const { planName, hasActiveTrial, canUsePremium, usage } = useAuth();
  const handleLogout = useLogout();

  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        const res = await API.get("/api/dashboard");
        setDashboards(res.data || []);
      } catch (error) {
        console.error("Failed to fetch dashboards:", error);
        setDashboards([]);
      }
    };

    const fetchUserInfo = async () => {
      try {
        const res = await API.get("/auth/user-info");
        
        if (res.data.username) {
          setUserInfo({
            username: res.data.username,
            role: res.data.role || "USER"
          });
          
          // Set theme from user preference
          const userTheme = res.data.themePreference === "dark";
          setDarkMode(userTheme);
        } else {
          // Fallback to localStorage if API fails
          setUserInfo({
            username: localStorage.getItem("username") || "User",
            role: localStorage.getItem("userRole") || "USER"
          });
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
        // Fallback to localStorage
        setUserInfo({
          username: localStorage.getItem("username") || "User",
          role: localStorage.getItem("userRole") || "USER"
        });
      }
    };
    
    fetchDashboards();
    fetchUserInfo();
  }, []);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    setSettingsOpen(false);
  };

  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
    setDropdownOpen(false);
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/settings/profile");
  };

  const goToSettings = () => {
    navigate("/settings");
  };

  const handleChangePasswordClick = () => {
    setDropdownOpen(false);
    navigate("/change-password");
  };

  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    const theme = newDarkMode ? 'dark' : 'light';
    
    try {
      const response = await API.post('/auth/update-theme', {
        theme: theme
      });

      if (response.data.message) {
        setDarkMode(newDarkMode);
        setSettingsOpen(false);
        console.log('Theme preference updated successfully');
      } else {
        console.error('Failed to update theme preference');
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
      setDarkMode(newDarkMode);
      localStorage.setItem('darkMode', newDarkMode.toString());
      setSettingsOpen(false);
    }
  };

  const handleDashboardSelect = (dashboardName) => {
    setSelectedDashboard(dashboardName);
  };

  const handleViewDashboard = () => {
    if (selectedDashboard) {
      const dashboard = dashboards.find(d => d.name === selectedDashboard);
      if (dashboard) {
        navigate(`/dashboards/${encodeURIComponent(dashboard.name)}/dashboard-viewer`, {
          state: timeSelection
        });
      }
    }
  };

  const handleEditDashboard = () => {
    if (selectedDashboard) {
      const dashboard = dashboards.find(d => d.name === selectedDashboard);
      if (dashboard) {
        navigate(`/dashboards/${encodeURIComponent(dashboard.name)}/dashboard-builder`, {
          state: timeSelection
        });
      }
    }
  };

  const handleCreateNew = () => {
    navigate('/dashboard-builder');
  };

  const handlePrintPage = useCallback(() => {
    window.print();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.user-dropdown')) {
        setDropdownOpen(false);
      }
      if (settingsOpen && !event.target.closest('.settings-dropdown')) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, settingsOpen]);

  return (
    <div className={`dashboard-container dashboards-page ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Blue Header Stripe */}
      <header className="dashboard-header commray-no-print">
        <div className="header-content">
          {/* Commray Branding - Left Side */}
          <div className="brand-section">
            <h1 className="brand-name">Commray</h1>
          </div>

          {/* Dashboard Title - Center */}
          <div className="center-title">
            <h2 className="dashboard-title-header">Dashboards</h2>
            <button
              type="button"
              className="dashboards-header-link"
              onClick={() => navigate("/alerts")}
            >
              Alerts
            </button>
            <span className="dashboards-header-link" style={{ cursor: "default", opacity: 0.95 }}>
              Plan: {(planName || "free").toUpperCase()}{hasActiveTrial ? " (Trial)" : ""}
            </span>
            {!canUsePremium && (
              <button
                type="button"
                className="dashboards-header-link"
                onClick={() => navigate("/settings/billing")}
              >
                Upgrade
              </button>
            )}
          </div>

          {/* Right Side Controls */}
          <div className="right-controls">
            <AskCommrayAi />
            {/* Settings Button */}
            <div className="settings-dropdown">
              <button className="settings-button" onClick={toggleSettings}>
                <span className="settings-icon">⚙️</span>
              </button>

              {settingsOpen && (
                <div className="settings-menu">
                  <div className="settings-header">
                    <h4>Settings</h4>
                  </div>
                  <div className="settings-divider"></div>
                  <div
                    className="settings-item"
                    onClick={() => {
                      setSettingsOpen(false);
                      goToSettings();
                    }}
                  >
                    <span className="settings-item-icon">⚙</span>
                    <span>Open Settings</span>
                  </div>
                  <div className="settings-item" onClick={toggleDarkMode}>
                    <span className="settings-item-icon">{darkMode ? '☀️' : '🌙'}</span>
                    <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </div>
                  <UserTimezoneSelect />
                  <div className="settings-divider" />
                  <div
                    className="settings-item"
                    onClick={() => {
                      setSettingsOpen(false);
                      handlePrintPage();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">🖨</span>
                    <span>Print page</span>
                  </div>
                </div>
              )}
            </div>

            {/* User Dropdown */}
            <div className="user-dropdown">
              <button className="user-button" onClick={toggleDropdown}>
                <div className="user-avatar">
                  {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="user-info">
                  <span className="user-name">{userInfo.username}</span>
                  <span className="user-role">{userInfo.role?.replace('ROLE_', '')}</span>
                </div>
                <div className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>
                  ▼
                </div>
              </button>

              {dropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">
                        {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div>
                        <div className="dropdown-name">{userInfo.username}</div>
                        <div className="dropdown-role">{userInfo.role?.replace('ROLE_', '')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item" onClick={handleProfileClick}>
                    <span className="dropdown-icon">👤</span>
                    Settings
                  </div>
                  <div className="dropdown-item" onClick={handleChangePasswordClick}>
                    <span className="dropdown-icon">🔒</span>
                    Change Password
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item logout-item" onClick={handleLogout}>
                    <span className="dropdown-icon">🚪</span>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {usage?.limitReached && (
        <div
          className="commray-no-print"
          style={{
            background: "#fef3c7",
            color: "#92400e",
            padding: "0.75rem 1.25rem",
            textAlign: "center",
            borderBottom: "1px solid #fcd34d",
          }}
          role="status"
        >
          {usage.message || "Free plan limits reached. Please upgrade to continue ingesting logs."}{" "}
          <button
            type="button"
            onClick={() => navigate("/settings/billing")}
            style={{
              background: "none",
              border: "none",
              color: "#2563eb",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
              padding: 0,
            }}
          >
            Upgrade
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="dashboard-content">
        <div className="dashboard-title">
          <h2>Manage Dashboards</h2>
          <p>Select a dashboard to view or edit, or create a new one</p>
        </div>

        {/* Dashboard Selection */}
        <div className="dashboard-selection-container">
          <div className="dashboard-selection-card">
            <h3>Select Dashboard</h3>
            <div className="dashboard-dropdown-container">
              <select 
                className="dashboard-dropdown"
                value={selectedDashboard}
                onChange={(e) => handleDashboardSelect(e.target.value)}
              >
                <option value="">Choose a dashboard...</option>
                {dashboards.map((dashboard, index) => (
                  <option key={index} value={dashboard.name}>
                    {dashboard.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Time Range Selection */}
            <div className="time-range-section manage-time-range">
              <div className="sidebar-dropdown-header" onClick={() => setTimeRangeOpen(!timeRangeOpen)}>
                <h4>⏱️ Time Range</h4>
                <span className={`dropdown-arrow ${timeRangeOpen ? 'open' : ''}`}>▼</span>
              </div>
              {timeRangeOpen && (
                <DashboardTimeRangeControls
                  selection={timeSelection}
                  onChange={updateTimeSelection}
                />
              )}
            </div>
            
            {selectedDashboard && (
              <div className="dashboard-actions">
                <button 
                  className="action-button primary"
                  onClick={handleViewDashboard}
                >
                  📊 View Dashboard
                </button>
                <button 
                  className="action-button secondary"
                  onClick={handleEditDashboard}
                >
                  ✏️ Edit Dashboard
                </button>
              </div>
            )}
          </div>

          <div className="create-dashboard-card">
            <h3>Create New Dashboard</h3>
            <p>Start building a new monitoring dashboard from scratch</p>
            <button 
              className="action-button create"
              onClick={handleCreateNew}
            >
              ➕ Create New Dashboard
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions quick-actions--wide">
          <div className="nav-card" onClick={() => navigate('/logs')}>
            <div className="nav-icon">📋</div>
            <div className="nav-content">
              <h3>Log Explorer</h3>
              <p>Search, filter, and analyze application logs in real-time</p>
            </div>
          </div>
          <div className="nav-card" onClick={() => navigate('/alerts')}>
            <div className="nav-icon">🔔</div>
            <div className="nav-content">
              <h3>Alerts</h3>
              <p>Email recipients, alert templates for errors, outages, and high volume</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboards;
